import * as React from 'react';
import { Skeleton } from '@chess/api-skeleton';
import { Pie, PieLegend, TextSkeleton, type CardProps, type FilterLink, type PieSlice } from '@chess/api-insights/charts';
import { CardAside, CardEmpty, CardNote } from '@chess/api-dashboard/react';
import { MIDDLEGAME_RESULT_VIEWS, middlegameCellText } from '@chess/api-ending-case';
import '@chess/api-ending-case/colors.css';
import { MIDDLEGAME_RESULTS_HELP } from './help.tsx';
import { middlegamesOf, type MiddlegameResultsResult } from './MiddlegameResultsAggregator.ts';
import './middlegame-results.css';

// Middlegame results: every scanned game of the period that reached a
// middlegame, in one of five slices from how the middlegame started (winning
// at the Winning setting or more, lost at minus it or less, level between)
// and how it ended (the position taken into the ending, or the result when
// the game finished in the middlegame): Ahead, Comeback (ended level or
// winning from a lost start), Level, Blown advantage (ended level or lost
// from a winning start), Behind. The Ending results card's grid and colours,
// so the two pies read alike. A slice's tooltip names its start × end cells;
// every slice and legend entry opens exactly those games in the library
// (`middlegame-result`). The note gives the scope. Drawn from props only:
// the card's body, which the section draws in the dashboard's frame
// (`MIDDLEGAME_RESULTS_CARD`, `registerCard`).

export type MiddlegameResultsCardProps = CardProps<{ result: MiddlegameResultsResult }, {
  /** Opens the library filtered to a slice's games. */
  link?: FilterLink;
}>;

const SIZE = 96;
const games = (n: number) => `${n} ${n === 1 ? 'game' : 'games'}`;

function slicesOf(r: MiddlegameResultsResult): PieSlice[] {
  return MIDDLEGAME_RESULT_VIEWS.map((v) => {
    const cells = r.cells[v.result].map((c) => `${middlegameCellText(c.start, c.end)} ${c.count}`).join(' · ');
    return {
      key: v.result,
      label: v.label,
      count: r.byResult[v.result],
      colour: v.colour,
      title: `${v.label}: ${cells || v.description} · opens those games`,
      filter: { middlegameResult: v.result },
    };
  });
}

const LoadingBody: React.FC = () => (
  <div className="donut-wrap">
    <Skeleton loading variant="circular" width={`${SIZE / 16}rem`} height={`${SIZE / 16}rem`} label="Loading your middlegame results" />
    <ul className="legend">
      {MIDDLEGAME_RESULT_VIEWS.map((v) => (
        <li key={v.result}>
          <span className="legend-swatch" style={{ background: v.colour }} />
          {v.label}
          <span className="legend-value">
            <Skeleton loading inline variant="text" shape="12 · 34%" />
          </span>
        </li>
      ))}
    </ul>
  </div>
);

/** The frame's title and help topic: the registration's and the stories'. */
export const MIDDLEGAME_RESULTS_CARD = { title: 'Middlegame results', help: MIDDLEGAME_RESULTS_HELP };
const ASIDE = 'from the start of the middlegame';

export const MiddlegameResultsCard: React.FC<MiddlegameResultsCardProps> = (props) => {
  if (props.loading)
    return (
      <>
        <CardAside>{ASIDE}</CardAside>
        <div className="middlegame-results-body">
          <LoadingBody />
        </div>
        <CardNote>
          <TextSkeleton sample="42 of 188 games reached a middlegame" />
        </CardNote>
      </>
    );
  const { result, link } = props;
  const middlegames = middlegamesOf(result);
  if (!middlegames)
    return (
      <>
        <CardAside>{ASIDE}</CardAside>
        <CardEmpty>No middlegames in the last 30 days.</CardEmpty>
      </>
    );
  const slices = slicesOf(result);
  return (
    <>
      <CardAside>{ASIDE}</CardAside>
      <div className="middlegame-results-body">
        <div className="donut-wrap">
          <Pie slices={slices} size={SIZE} link={link} />
          <PieLegend slices={slices} link={link} />
        </div>
      </div>
      <CardNote>{`${middlegames} of ${games(result.games)} reached a middlegame`}</CardNote>
    </>
  );
};
